import { useSyncExternalStore } from "react";
import type { TailMsg } from "@/hooks/useAiConcierge";

// When the renter last looked at the concierge transcript, per tenant, so the
// inbox row can show an unread dot for a team reply that arrived while they
// were elsewhere. A tiny localStorage-backed store shared by every reader.

const key = (tenantId: string) => `msth_concierge_seen_${tenantId}`;
const listeners = new Set<() => void>();

export const readConciergeSeen = (tenantId: string): string | null => {
  try {
    return localStorage.getItem(key(tenantId));
  } catch {
    return null;
  }
};

/** Record that everything up to `at` (ISO) has been seen; never moves backwards. */
export const markConciergeSeen = (tenantId: string, at: string) => {
  const current = readConciergeSeen(tenantId);
  if (current && current >= at) return;
  try {
    localStorage.setItem(key(tenantId), at);
  } catch {
    /* storage unavailable, readers just keep the in-memory state */
  }
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useConciergeSeen = (tenantId: string): string | null =>
  useSyncExternalStore(subscribe, () => readConciergeSeen(tenantId), () => null);

/** The newest transcript line is a team reply the renter has not seen yet. */
export const hasUnseenTeamReply = (last: TailMsg | null, seenAt: string | null): boolean =>
  !!last && last.role === "agent" && !!last.at && (!seenAt || last.at > seenAt);
